import type { Brand } from "@/utils/theme/brand";

import { forEveryGuestmakerClient } from "./guestmakerClients";

export interface ClientTheme {
  name: string;
  /** When set, the theme engine stamps <html data-ul-brand="…"> so shared
   *  layout can render a brand-specific shell. */
  brand?: Brand;
  variables: Record<string, string>;
}

// Guestmaker: ink primary button, fuchsia accents, split-screen brand shell
const guestmakerTheme: ClientTheme = {
  name: "Guestmaker",
  brand: "guestmaker",
  variables: {
    "--ul-theme-color-primary-button": "#0F1117",
    "--ul-theme-color-primary-button-label": "#FFFFFF",
    "--ul-theme-color-base-focus-color": "#ec71fe",
    "--ul-theme-color-links-focused-components": "#0F1117",
    "--ul-theme-color-spinner": "#ec71fe",
    "--ul-theme-color-header": "#0F1117",
    "--ul-theme-color-body-text": "#4B5563",
    "--ul-theme-color-input-border": "#E5E7EB",
    "--ul-theme-color-input-labels-placeholders": "#6B7280",
    "--ul-theme-color-error": "#DC2626",
    "--ul-theme-color-success": "#d946ef",
    "--ul-theme-color-widget-background": "transparent",
    "--ul-theme-page-bg-background-color": "#FAFAF8",
    "--ul-theme-border-button-border-radius": "10px",
    "--ul-theme-border-input-border-radius": "10px",
    "--ul-theme-border-widget-corner-radius": "0px",
    "--ul-theme-border-links-border-radius": "6px",
    "--ul-theme-widget-header-text-alignment": "left",
    "--ul-theme-widget-logo-position": "none",
  },
};

// Hotelinking Admin: the admin console's own look (neutral greys, coffee
// primary in light, cream in dark). Theme-aware like WiFiBot, so colours live
// in admin.css and only the structural radii and the brand flag are set here.
const adminTheme: ClientTheme = {
  name: "Admin",
  brand: "admin",
  variables: {
    "--ul-theme-border-button-border-radius": "10px",
    "--ul-theme-border-input-border-radius": "10px",
    "--ul-theme-border-widget-corner-radius": "0px",
    "--ul-theme-border-links-border-radius": "6px",
  },
};

// WiFiBot: theme-aware brand shell. Colours live in wifibot.css
// (prefers-color-scheme), so only the structural border radii and the brand
// flag are set here — theme-dependent CSS variables set inline would defeat
// the media-query overrides.
const wifibotTheme: ClientTheme = {
  name: "WiFiBot",
  brand: "wifibot",
  variables: {
    "--ul-theme-border-button-border-radius": "10px",
    "--ul-theme-border-input-border-radius": "10px",
    "--ul-theme-border-widget-corner-radius": "0px",
    "--ul-theme-border-links-border-radius": "6px",
  },
};

export const CLIENT_THEME_MAP: Record<string, ClientTheme> = {
  // ⚠️ EVERY GuestMaker client, derived — not the web one typed by hand.
  // The iOS operator app is a SECOND GuestMaker client, and listing them
  // separately here is how it came to render the Hotelinking shell on the one
  // screen where somebody decides whether they are in the right place.
  ...forEveryGuestmakerClient(guestmakerTheme),
  // WiFiBot
  "8BRbe3Aqi76CJgYRi3TSdQasaM51MCrW": wifibotTheme,
  // Admin
  p3Izr9GxZ3S3xDO24T0JCf2U5WE7lKvW: adminTheme,
};

export function getClientTheme(
  clientId: string | undefined
): ClientTheme | undefined {
  if (!clientId) return undefined;
  return CLIENT_THEME_MAP[clientId];
}
